import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningPath, runningUrl } from '../utils/paths';
import { normalizeClubSport } from '../config/clubSports';
import { useMyClubMemberships, getMembershipStatusForClub } from './MembershipStatusBanner';
import './RunningClubHome.css';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBA';
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const RunningClubHome = () => {
  const { id } = useParams();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canManage, setCanManage] = useState(false);
  const { memberships } = useMyClubMemberships(isAuthenticated ? user?.id : null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(id);
      setClub(clubData);
      if (normalizeClubSport(clubData.sport) !== 'RUNNING') {
        return;
      }
      const [routeData, practiceData] = await Promise.all([
        api.getRunningRoutes(id, true),
        api.getUpcomingRunningPractices(id, 8),
      ]);
      setRoutes(Array.isArray(routeData) ? routeData : []);
      setPractices(Array.isArray(practiceData) ? practiceData : []);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      setCanManage(!!(isOwner || isAdmin()));
    } catch (err) {
      setError(err.message || 'Failed to load running club');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="loading">Loading club...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!club) {
    return <div className="error-message">Club not found</div>;
  }

  if (normalizeClubSport(club.sport) !== 'RUNNING') {
    return <Navigate to={`/soccer/clubs/${id}`} replace />;
  }

  const membership = getMembershipStatusForClub(memberships, id);

  return (
    <div className="running-club-home">
      <header className="running-club-home__hero">
        <div
          className="running-club-home__hero-bg"
          style={
            club.imageUrl
              ? { backgroundImage: `url(${club.imageUrl})` }
              : undefined
          }
          aria-hidden="true"
        />
        <div className="running-club-home__hero-shade" aria-hidden="true" />
        <div className="running-club-home__hero-content">
          <p className="running-club-home__eyebrow">Running club</p>
          <h1>{club.name}</h1>
          {club.description && <p className="running-club-home__desc">{club.description}</p>}
          <div className="running-club-home__actions">
            <Link to={runningPath(`/clubs/${id}/routes`)} className="btn btn-secondary">
              Browse routes
            </Link>
            <Link to={runningPath(`/clubs/${id}/practices`)} className="btn btn-primary">
              Group practices
            </Link>
            {canManage && (
              <Link to={runningPath(`/clubs/${id}/admin`)} className="btn btn-secondary">
                Club admin
              </Link>
            )}
          </div>
          {membership?.status === 'PENDING' && (
            <p className="running-club-home__note">Your join request is pending approval.</p>
          )}
        </div>
      </header>

      <section className="running-club-home__section">
        <div className="running-club-home__section-head">
          <h2>Upcoming practices</h2>
          <p>Vote availability and meet at the published gathering spot.</p>
        </div>
        {practices.length === 0 ? (
          <p className="running-club-home__empty">No upcoming group runs yet.</p>
        ) : (
          <div className="running-club-home__list">
            {practices.map((practice) => (
              <article key={practice.id} className="running-practice-card">
                <div>
                  <h3>{practice.title}</h3>
                  <p className="running-practice-card__meta">{formatDate(practice.practiceDate)}</p>
                  {practice.gatheringLocation && (
                    <p className="running-practice-card__meta">Meet: {practice.gatheringLocation}</p>
                  )}
                  {practice.route?.title && (
                    <p className="running-practice-card__meta">Route: {practice.route.title}</p>
                  )}
                </div>
                <div className="running-practice-card__actions">
                  <Link
                    to={runningPath(`/practices/${practice.id}`)}
                    className="btn btn-primary"
                  >
                    Vote &amp; details
                  </Link>
                  {practice.shareToken && (
                    <a
                      href={runningUrl(`/public/practices/${practice.shareToken}`)}
                      className="btn btn-secondary"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Share link
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="running-club-home__section">
        <div className="running-club-home__section-head">
          <h2>Published routes</h2>
          <p>Pick a track for practice — admins publish distance, surface, and map links.</p>
        </div>
        {routes.length === 0 ? (
          <p className="running-club-home__empty">No published routes yet.</p>
        ) : (
          <div className="running-club-home__routes">
            {routes.map((route) => (
              <article key={route.id} className="running-route-card">
                <h3>{route.title}</h3>
                {route.distanceKm != null && (
                  <p className="running-route-card__stat">{route.distanceKm} km</p>
                )}
                <p className="running-route-card__meta">
                  {[route.surface, route.difficulty].filter(Boolean).join(' · ') || 'Route'}
                </p>
                {route.startLocation && (
                  <p className="running-route-card__meta">Start: {route.startLocation}</p>
                )}
                {route.mapUrl && (
                  <a href={route.mapUrl} target="_blank" rel="noreferrer">
                    Open map
                  </a>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default RunningClubHome;
